import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
} from '@pnl/ui';
import { PnlApiError, type PendingRestartItem } from '@pnl/sdk';
import { api } from '../lib/api';

export function PendingRestartsPage() {
  const query = useQuery({
    queryKey: ['admin', 'pending-restarts'],
    queryFn: () => api.listPendingRestarts(),
    refetchInterval: 30_000,
  });

  const items = query.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Operação</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Restarts pendentes
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Traders que pagaram/solicitaram restart. Reative a subconta na Nelogica; quando o trader
          pedir a ativação em "Minhas Contas", aprove aqui para reiniciar a conta do zero.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Fila ({items.length}
            {query.isLoading ? '…' : ''})
          </CardTitle>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<RotateCcw size={28} />}
              title="Nenhum restart pendente"
              description="Restarts pagos ou inclusos aguardando ativação aparecem aqui."
            />
          ) : (
            <div className="divide-ink-100 divide-y">
              {items.map((item) => (
                <PendingRestartRow key={item.accountId} item={item} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Alert tone="info" title="Como funciona">
        Trader paga/solicita o restart → a conta entra nesta fila e você reativa a subconta na
        Nelogica (editando o número da conta na tela de Contas, se ele mudar). O trader então clica
        "Ativar conta" em Minhas Contas e você aprova aqui — só então a conta reinicia do zero.
      </Alert>
    </div>
  );
}

function PendingRestartRow({ item }: { item: PendingRestartItem }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: () => api.approveRestartActivation(item.accountId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'pending-restarts'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'accounts'] });
    },
    onError: (e: unknown) =>
      setError(e instanceof PnlApiError ? e.api.message : 'Falha ao aprovar a ativação.'),
  });

  return (
    <div className="flex items-center justify-between gap-4 py-4">
      <div className="flex-1">
        <p className="text-ink-900 text-sm font-semibold">
          {item.traderName || item.traderEmail}{' '}
          <span className="text-ink-500 font-normal">· {item.traderEmail}</span>
        </p>
        <p className="text-ink-500 mt-0.5 text-xs">
          {item.accountLabel}
          {item.subAccountNelogicaId ? ` · subconta ${item.subAccountNelogicaId}` : ''}
          {item.blownAt
            ? ` · reprovada em ${new Date(item.blownAt).toLocaleDateString('pt-BR')}`
            : ''}
        </p>
        {error && <p className="text-danger-600 mt-1 text-xs">{error}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <Badge tone={item.type === 'pago' ? 'success' : 'brand'}>Restart {item.type}</Badge>
        {item.restartsUsed > 0 && (
          <span className="text-ink-400 text-2xs">{item.restartsUsed}º restart</span>
        )}
        {item.restartActivationRequested ? (
          <Button
            size="sm"
            leadingIcon={<RotateCcw size={13} />}
            loading={approve.isPending}
            onClick={() => {
              setError(null);
              approve.mutate();
            }}
          >
            Aprovar e ativar
          </Button>
        ) : (
          <span className="text-ink-400 text-2xs">Aguardando o trader ativar</span>
        )}
      </div>
    </div>
  );
}
